// THE INVOICE'S OWN PHOTO, ON THE CHARGE'S ROW.
//
// The document belongs to the CHARGE, not to a payment: an invoice is collected in
// several partial payments and there is only ever one document behind them, so hanging
// the scan off a payment would store the same file repeatedly and leave "show me the
// invoice" with no single answer (migration 051).
//
// REUSES `FileActions`, the same component the publication screenshots and the signed
// agreements use, rather than growing a third set of file buttons. That component is not
// merely convenient — its open-a-signed-URL behaviour encodes two real failures that
// were fixed once (a synthetic anchor removed before the navigation ran; a blank tab
// opened before the await, which made `noopener` fatal). A second copy of it here would
// be a liability. `getUrl` is the one thing that varies, and invoices pass their own.
//
// ATTACHING IS OFFERED HERE TOO, not only when the charge is recorded. A partner often
// has the invoice number before the scan — recording the money must never wait on having
// the document in hand — so a row with nothing attached shows an upload control instead
// of a gap.
//
// DELETE DESTROYS THE R2 OBJECT WITH THE ROW and leaves the charge untouched: the money
// is not the document, and removing a mis-uploaded photo must never look like voiding an
// invoice. That is why it confirms first and says which of the two it is about.
import { useRef, useState } from 'react';
import { invoicesApi } from '../../api';
import he from '../../i18n/he';
import { FileActions } from '../../components/FileList';
import IconButton from '../../components/IconButton';
import { UploadIcon } from '../../components/icons';
import { messageForError } from '../../i18n/errors';

const t = he.collections.board;

export default function ChargeFiles({ row, onChanged }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const files = row.files || [];

  const upload = async (file) => {
    if (!file || busy) return;
    setBusy(true);
    try {
      const body = new FormData();
      body.append('file', file);
      await invoicesApi.uploadFile(row.invoiceId, body);
      onChanged();
    } catch (e) {
      window.alert(messageForError(e));
    } finally {
      setBusy(false);
      // Clear it, so picking the SAME file again still fires a change event.
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const remove = async (file) => {
    // Names the document, and says plainly that the charge survives — the one thing a
    // partner needs to be sure of before deleting anything on a money screen.
    if (!window.confirm(t.confirmDeleteFile(file.original_name))) return;
    try {
      await invoicesApi.removeFile(file.id);
      onChanged();
    } catch (e) {
      window.alert(messageForError(e));
    }
  };

  return (
    <span className="file-list">
      {files.map((f) => (
        <span key={f.id} className="file-row">
          <span className="file-name" title={f.original_name}>{f.original_name}</span>
          <FileActions file={f} onRemove={remove} getUrl={invoicesApi.fileDownloadUrl} />
        </span>
      ))}
      {/* The button and the input are SIBLINGS, not nested. A <button> inside a <label>
          is two interactive elements claiming the same click, and the label's own
          activation then fires the picker a second time. The button drives the hidden
          input through a ref instead, which is unambiguous. */}
      <IconButton
        icon={<UploadIcon />}
        label={files.length > 0 ? t.attachAnother : t.attachInvoiceFile}
        onClick={() => inputRef.current && inputRef.current.click()}
      />
      <input
        ref={inputRef}
        type="file"
        hidden
        disabled={busy}
        onChange={(e) => upload(e.target.files[0])}
      />
    </span>
  );
}
